import { Button, CircularProgress, Tooltip, useTheme } from "@mui/material";
import { TriangleAlert } from "lucide-react";
import { destructiveButtonSx } from "./field-shell";

/**
 * §Escalate-overdue: the one Escalate control, shared by every surface that shows an overdue step.
 *
 * Destructive styling rather than secondary: escalating is not a routine step action — it puts the case
 * in front of a manager — and it sits beside a red "Overdue 3h" label it should read as continuous with.
 *
 * ⚠ `stopPropagation` lives HERE, not in the hosts. Every surface that renders this button is itself
 * clickable — the dashboard row navigates to the lead, the step card toggles its accordion, a board card
 * opens the case — so a click that bubbled would fire the escalation AND navigate away from the modal it
 * just opened. Keeping it in the component means a new host cannot forget it.
 */
const EscalateButton = ({
  onClick,
  busy,
  dense,
  title = "Escalate this overdue task",
}: {
  onClick: () => void;
  busy: boolean;
  /** tighter geometry for a dense host (a board card), where the standard height crowds the row */
  dense?: boolean;
  title?: string;
}) => {
  const theme = useTheme();

  return (
    <Tooltip title={title}>
      {/* span: MUI cannot attach a tooltip to a disabled button */}
      <span style={{ display: "inline-flex", flexShrink: 0 }}>
        <Button
          variant="outlined"
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            onClick();
          }}
          startIcon={
            busy ? (
              <CircularProgress size={dense ? 9 : 11} color="inherit" />
            ) : (
              <TriangleAlert size={dense ? 11 : 13} />
            )
          }
          sx={{
            ...destructiveButtonSx(theme),
            height: dense ? 20 : 24,
            px: dense ? 0.75 : 1,
            fontSize: dense ? 9.5 : 11,
            lineHeight: 1,
            "& .MuiButton-startIcon": { mr: 0.5, ml: 0 },
          }}
        >
          Escalate
        </Button>
      </span>
    </Tooltip>
  );
};

export default EscalateButton;
